"use client";

import { Card, CircularProgress, FormControlLabel, Switch } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import {
  buildTemplatePreview,
  getCustomerTemplateStatusLabel,
  getCustomerTemplateTypeLabel,
  isLegacyCustomerTemplateType,
} from "@/constants/message-template.constants";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetMessageTemplateById from "@/hooks/requests/message-template/use-get-message-template-by-id";
import useGetMessageTemplateTags from "@/hooks/requests/message-template/use-get-message-template-tags";
import useUpdateMessageTemplate from "@/hooks/requests/message-template/use-update-message-template";
import useMutate from "@/hooks/use-mutate";
import { MessageTemplateMessages } from "@/messages/message-template/message-template.messages";

import { TemplateVariablesBlock } from "./components/TemplateVariablesBlock";
import { TemplatePreview } from "./components/TemplatePreview";

type Props = {
  messages: MessageTemplateMessages;
};

const LOADING_SPINNER_SIZE = 50;

function MessageTemplateFormPage({ messages }: Props) {
  const { templateId } = useParams();
  const router = useNavigate();
  const { lang } = useLanguage();
  const { mutate } = useMutate();

  const { data: template, isLoading, error } = useGetMessageTemplateById(Number(templateId));
  const { update, isLoading: isSaving } = useUpdateMessageTemplate();
  const { data: variableGroups } = useGetMessageTemplateTags(template?.type);

  const [text, setText] = useState("");
  const [notificationOn, setNotificationOn] = useState(true);

  const textRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (template) {
      setText(template.text || "");
      setNotificationOn(template.notificationOn ?? true);
    }
  }, [template]);

  const insertVariable = (variable: string) => {
    const value = text;
    const setValue = setText;
    const element = textRef.current;

    const start = element?.selectionStart ?? value.length;
    const end = element?.selectionEnd ?? value.length;

    setValue(`${value.slice(0, start)}${variable}${value.slice(end)}`);

    requestAnimationFrame(() => {
      element?.focus();
      const cursor = start + variable.length;
      element?.setSelectionRange(cursor, cursor);
    });
  };

  const handleSave = async () => {
    if (!template) return;

    await update({
      _id: template.id,
      formData: { text, messageTemplate: template.messageTemplate ?? "", notificationOn },
      successMessage: messages.updateSuccess,
      errorMessage: messages.updateError,
      onSuccess: () => {
        void mutate("getMessageTemplate");
        router(ROUTE_URLS.messageTemplates(lang));
      },
    });
  };

  const readOnlyFields = template
    ? [
        { label: messages.idColumn, value: String(template.id) },
        { label: messages.platformColumn, value: template.platformName || "-" },
        { label: messages.typeColumn, value: getCustomerTemplateTypeLabel(template.type) },
        { label: messages.statusColumn, value: getCustomerTemplateStatusLabel(template.status) },
        { label: messages.rowDateColumn, value: template.rowDate || "-" },
      ]
    : [];

  return (
    <PageLayout messages={messages} title={messages.updatePageTitle}>
      <PageContainer>
        <Suspense
          isSuspended={isLoading}
          fallback={<CircularProgress size={LOADING_SPINNER_SIZE} />}
        >
          <>
            {!!error && !template && (
              <AlertBlock
                title={messages.errorToLoad}
                description={error.message}
                alt={messages.notFoundAlt}
              />
            )}

            {!!template && (
              <Card sx={{ mt: 2 }}>
                <MDBox p={3}>
                  {isLegacyCustomerTemplateType(template.type) && (
                    <MDBox mb={2}>
                      <MDTypography variant="button" color="warning" fontWeight="bold">
                        {messages.legacyTypeWarning}
                      </MDTypography>
                    </MDBox>
                  )}

                  <Grid container spacing={2}>
                    {readOnlyFields.map((field) => (
                      <Grid key={field.label} size={{ xs: 12, sm: 6, md: 4 }}>
                        <MDTypography variant="caption" color="text">
                          {field.label}
                        </MDTypography>
                        <MDTypography variant="button" fontWeight="medium" display="block">
                          {field.value}
                        </MDTypography>
                      </Grid>
                    ))}
                  </Grid>

                  <MDBox mb={3} />

                  <MDBox mb={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={notificationOn}
                          onChange={(e) => setNotificationOn(e.target.checked)}
                        />
                      }
                      label={messages.notificationOnColumn}
                    />
                  </MDBox>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 7 }}>
                      <TemplateVariablesBlock
                        title={messages.availableVariablesTitle}
                        description={messages.availableVariablesDescription}
                        groups={variableGroups}
                        onSelect={(variable) => insertVariable(variable)}
                      />

                      <MDBox mt={2}>
                        <MDInput
                          fullWidth
                          multiline
                          minRows={6}
                          label={messages.textLabel}
                          value={text}
                          onChange={(e) => setText(e.target.value)}
                          inputRef={textRef}
                        />
                      </MDBox>

                    </Grid>

                    <Grid size={{ xs: 12, md: 5 }}>
                      <TemplatePreview
                        title={messages.previewTitle}
                        content={buildTemplatePreview(text)}
                      />
                    </Grid>
                  </Grid>

                  <MDBox mt={3} display="flex" gap={1} justifyContent="flex-end">
                    <MDButton
                      variant="outlined"
                      color="dark"
                      onClick={() => router(ROUTE_URLS.messageTemplates(lang))}
                    >
                      {messages.cancelButton}
                    </MDButton>
                    <MDButton
                      variant="gradient"
                      color="dark"
                      isLoading={isSaving}
                      onClick={handleSave}
                    >
                      {messages.updateButton}
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            )}
          </>
        </Suspense>
      </PageContainer>
    </PageLayout>
  );
}

export default MessageTemplateFormPage;
